import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Native HTML checkbox styled as a switch (left/right pill) — chosen over a
 * Radix wrapper to keep this library dependency-light. Use for boolean
 * settings (enable channel, accept marketing emails, etc.).
 *
 * Off-state track uses `bg-input` (a clean grey), NOT `bg-secondary` (the pale
 * blue-tinted background token) — the pale blue read as an "active-ish" state
 * next to the primary-blue on state, so users couldn't tell at a glance
 * whether a product/setting was hidden or active (NEA-6124).
 */
const Toggle = React.forwardRef(({ className, checked, onChange, label, disabled, ...props }, ref) => (
	<label
		className={cn('inline-flex items-center gap-3 cursor-pointer select-none', disabled && 'opacity-50 cursor-not-allowed', className)}
	>
		<span className="relative inline-flex h-6 w-11 shrink-0">
			<input
				ref={ref}
				type="checkbox"
				className="peer sr-only"
				checked={!!checked}
				onChange={onChange}
				disabled={disabled}
				{...props}
			/>
			<span className="absolute inset-0 rounded-full bg-input transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2" />
			<span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-soft transition-transform peer-checked:translate-x-5" />
		</span>
		{label ? <span className="text-sm font-medium">{label}</span> : null}
	</label>
));
Toggle.displayName = 'Toggle';

export { Toggle };
